import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import {
  ReviewTask,
  ReviewTaskAssigneeOption,
  ReviewTaskStatus,
  ReviewTaskWritePayload,
  REVIEW_TASK_PAGE_SIZE,
  cloneReviewTask,
  createReviewTask,
  deleteReviewTask,
  getReviewTaskAiBatch,
  listReviewTaskAssignees,
  listReviewTasks,
  setReviewTaskStatus,
  startReviewTaskAiBatch,
  stopReviewTaskAiBatch,
  updateReviewTask,
} from '../../../api/reviewTasks'
import { ProjectTracker, listProjectTrackers } from '../../../api/trackers'
import iconFile from '../../../assets/images/icon_file_blue.svg'
import iconPencil from '../../../assets/images/icon_pencil_blue.svg'
import iconPlus from '../../../assets/images/icon_plus.svg'
import iconMenu from '../../../assets/images/icon_vertical_dot_menu.svg'
import { useBuildingContext } from '../../../contexts/buildingContext'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { blue } from '../../../styles/colors'
import { ConfirmationModal } from '../../common/Confirmation/Confirmation'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { AddProjectButton } from '../buildings_page/components/AddProjectButton'

import { ReviewActivityLogModal } from './ReviewActivityLogModal'
import { ReviewAiAssistTab } from './ReviewAiAssistTab'
import {
  POINT_FILTER_OPTIONS,
  POINT_SEQUENCE_OPTIONS,
  STATUS_FILTERS,
  formatAiBatchStatus,
  formatCompactTaskDate,
  formatDurationHoursMinutes,
  formatMinutesPerPoint,
  formatReviewTaskProgress,
  isAiBatchRunning,
  isPendingReviewStatus,
  pointFilterLabel,
  pointSequenceLabel,
} from './reviewTaskFormat'
import {
  ActionButton,
  CancelButton,
  CellRelative,
  CheckLabel,
  CheckListPop,
  DateStack,
  DisabledCheck,
  DropdownToggle,
  EmptyState,
  FilterLink,
  FilterRow,
  HeaderRow,
  IconButton,
  LoadMoreWrap,
  Menu,
  MenuItem,
  PageWrap,
  ListBusyOverlay,
  SaveButton,
  StatsInline,
  StatsPopover,
  StatusDot,
  Table,
  TableScroll,
  Td,
  Th,
  Title,
} from './styles'
import { usePendingReviewTaskCount } from './usePendingReviewTaskCount'

type ReviewTaskListVariant = 'admin' | 'worker'

const BatchRunBtn = styled.button`
  border: none;
  background: ${blue};
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const BatchStopBtn = styled(BatchRunBtn)`
  border-radius: 4px;
`

interface ReviewTaskListProps {
  variant: ReviewTaskListVariant
}

interface FloorOption {
  id: number
  label: string
}

interface TrackerOption {
  id: number
  label: string
}

interface TaskDraft {
  floor_ids: number[]
  tracker_ids: number[]
  assignee_ids: number[]
  point_filter: ReviewTask['point_filter']
  point_sequence: ReviewTask['point_sequence']
  ai_assist: boolean
}

const draftFromTask = (task: ReviewTask): TaskDraft => ({
  floor_ids: [...task.floor_ids],
  tracker_ids: [...task.tracker_ids],
  assignee_ids: [...task.assignee_ids],
  point_filter: task.point_filter,
  point_sequence: task.point_sequence,
  ai_assist: task.ai_assist,
})

const toggleId = (ids: number[], id: number) =>
  ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id]

const idsLabel = (
  ids: number[],
  options: { id: number; label: string }[],
  emptyLabel: string,
  joinWithSpaces?: boolean,
) => {
  if (!ids.length) {
    return emptyLabel
  }
  const labels = ids.map(
    id => options.find(option => option.id === id)?.label ?? String(id),
  )
  return joinWithSpaces ? labels.join(' ') : labels.join(', ')
}

const trackerCountLabel = (ids: number[]) => {
  if (!ids.length) {
    return 'All'
  }
  return String(ids.length)
}

const trackerEditLabel = (ids: number[]) => {
  if (!ids.length) {
    return 'Select trackers'
  }
  return String(ids.length)
}

const byNewestTask = (tasks: ReviewTask[]) =>
  [...tasks].sort((a, b) => b.id - a.id)

const mergeTaskPage = (
  current: ReviewTask[],
  page: ReviewTask[],
  append: boolean,
) => {
  if (!append) {
    return byNewestTask(page)
  }
  const seen = new Set(current.map(task => task.id))
  return byNewestTask([...current, ...page.filter(task => !seen.has(task.id))])
}

export const ReviewTaskList = ({ variant }: ReviewTaskListProps) => {
  const isAdmin = variant === 'admin'
  const history = useHistory()
  const location = useLocation()
  const { generateReviewTaskPath, generateAdminReviewAiPath } =
    useGeneratedPaths()
  const { addNotification } = useNotifications()
  const { state: buildingState } = useBuildingContext()
  const { setPendingReviewTaskCount, adjustPendingReviewTaskCount } =
    usePendingReviewTaskCount()
  const projectId = buildingState.projectId as string

  const [statusFilter, setStatusFilter] = useState<ReviewTaskStatus | 'all'>(
    'all',
  )
  const listMode: 'status' | 'ai_assist' =
    isAdmin &&
    new URLSearchParams(location.search).get('tab') === 'ai_assist'
      ? 'ai_assist'
      : 'status'

  const setListMode = useCallback(
    (mode: 'status' | 'ai_assist') => {
      const params = new URLSearchParams(location.search)
      if (mode === 'ai_assist') {
        params.set('tab', 'ai_assist')
      } else {
        params.delete('tab')
      }
      const search = params.toString()
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      })
    },
    [history, location.pathname, location.search],
  )

  const [tasks, setTasks] = useState<ReviewTask[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [nextOffset, setNextOffset] = useState(0)
  const [trackers, setTrackers] = useState<TrackerOption[]>([])
  const [assignees, setAssignees] = useState<ReviewTaskAssigneeOption[]>([])
  const [editingId, setEditingId] = useState<number | null>(null)
  const [draft, setDraft] = useState<TaskDraft | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const [openStatsId, setOpenStatsId] = useState<number | null>(null)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [deleteTask, setDeleteTask] = useState<ReviewTask | null>(null)
  const [logTaskId, setLogTaskId] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [busyBatchId, setBusyBatchId] = useState<number | null>(null)
  const draftRef = useRef<TaskDraft | null>(null)
  const busyRef = useRef(false)
  const preferAllTrackersRef = useRef(false)
  draftRef.current = draft

  const runBusy = useCallback(async (work: () => Promise<void>) => {
    if (busyRef.current) {
      return
    }
    busyRef.current = true
    setBusy(true)
    try {
      await work()
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }, [])

  const floors: FloorOption[] = useMemo(() => {
    const projectFloors = (buildingState.projectData?.floors || []) as {
      id: number
      floor_code: string
      order?: number
    }[]
    return [...projectFloors]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(floor => ({ id: floor.id, label: floor.floor_code }))
  }, [buildingState.projectData?.floors])

  const loadTasks = useCallback(async () => {
    if (!projectId) {
      return
    }
    setLoading(true)
    try {
      const response = await listReviewTasks(projectId, {
        status: statusFilter,
        limit: REVIEW_TASK_PAGE_SIZE,
        offset: 0,
      })
      const page = response.data || []
      setTasks(byNewestTask(page))
      setHasMore(Boolean(response.meta?.next))
      setNextOffset(response.meta?.next_offset ?? page.length)
      if (response.meta?.pending_count != null) {
        setPendingReviewTaskCount(response.meta.pending_count)
      }
    } catch {
      addNotification('Unable to load review tasks', 'error')
    } finally {
      setLoading(false)
    }
  }, [addNotification, projectId, setPendingReviewTaskCount, statusFilter])

  const loadMoreTasks = useCallback(async () => {
    if (!projectId || loadingMore || !hasMore) {
      return
    }
    setLoadingMore(true)
    try {
      const response = await listReviewTasks(projectId, {
        status: statusFilter,
        limit: REVIEW_TASK_PAGE_SIZE,
        offset: nextOffset,
      })
      const page = response.data || []
      setTasks(current => mergeTaskPage(current, page, true))
      setHasMore(Boolean(response.meta?.next))
      setNextOffset(response.meta?.next_offset ?? nextOffset + page.length)
    } catch {
      addNotification('Unable to load more review tasks', 'error')
    } finally {
      setLoadingMore(false)
    }
  }, [
    addNotification,
    hasMore,
    loadingMore,
    nextOffset,
    projectId,
    statusFilter,
  ])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  const runningBatchKey = tasks
    .filter(task => isAiBatchRunning(task.ai_batch))
    .map(task => task.id)
    .join(',')

  useEffect(() => {
    if (!projectId || !runningBatchKey) return undefined
    const runningIds = runningBatchKey.split(',').map(Number)
    const id = window.setInterval(() => {
      void Promise.all(
        runningIds.map(async taskId => {
          try {
            const result = await getReviewTaskAiBatch(projectId, taskId)
            return [taskId, result.batch] as const
          } catch {
            return [taskId, null] as const
          }
        }),
      ).then(entries => {
        setTasks(prev =>
          prev.map(task => {
            const match = entries.find(([taskId]) => taskId === task.id)
            return match ? { ...task, ai_batch: match[1] } : task
          }),
        )
      })
    }, 2000)
    return () => window.clearInterval(id)
  }, [projectId, runningBatchKey])

  useEffect(() => {
    if (!projectId) {
      return
    }
    listProjectTrackers(projectId)
      .then(response => {
        const excluded = /^(wet\s*surface|clutter)$/i
        setTrackers(
          (response.data || [])
            .filter(
              (tracker: ProjectTracker) =>
                !excluded.test((tracker.type?.name || '').trim()),
            )
            .map((tracker: ProjectTracker) => ({
              id: tracker.id,
              label: tracker.type.name,
            })),
        )
      })
      .catch(() => {
        addNotification('Unable to load trackers', 'error')
      })
    if (isAdmin) {
      listReviewTaskAssignees(projectId)
        .then(setAssignees)
        .catch(() => {
          addNotification('Unable to load assignees', 'error')
        })
    }
  }, [addNotification, isAdmin, projectId])

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (
        target.closest('[data-review-popover]') ||
        target.closest('[data-review-save]')
      ) {
        return
      }
      setOpenMenuId(null)
      setOpenDropdown(null)
    }
    document.addEventListener('click', onPointerDown)
    return () => document.removeEventListener('click', onPointerDown)
  }, [])

  const replaceTask = (updated: ReviewTask) => {
    setTasks(current => {
      const exists = current.some(task => task.id === updated.id)
      if (!exists) {
        return [updated, ...current]
      }
      return current.map(task => (task.id === updated.id ? updated : task))
    })
  }

  const startEdit = (task: ReviewTask, preferAllTrackers = false) => {
    const nextDraft = draftFromTask(task)
    preferAllTrackersRef.current = preferAllTrackers
    if (preferAllTrackers && !nextDraft.tracker_ids.length && trackers.length) {
      nextDraft.tracker_ids = trackers.map(tracker => tracker.id)
      preferAllTrackersRef.current = false
    }
    setEditingId(task.id)
    setDraft(nextDraft)
    setOpenMenuId(null)
    setOpenDropdown(null)
  }

  useEffect(() => {
    if (
      !preferAllTrackersRef.current ||
      !trackers.length ||
      !draftRef.current ||
      draftRef.current.tracker_ids.length
    ) {
      return
    }
    preferAllTrackersRef.current = false
    setDraft(current =>
      current && !current.tracker_ids.length
        ? {
            ...current,
            tracker_ids: trackers.map(tracker => tracker.id),
          }
        : current,
    )
  }, [trackers])

  const cancelEdit = () => {
    preferAllTrackersRef.current = false
    setEditingId(null)
    setDraft(null)
    setOpenDropdown(null)
  }

  const saveEdit = async () => {
    const currentDraft = draftRef.current
    if (!editingId || !currentDraft) {
      return
    }
    if (!currentDraft.tracker_ids.length) {
      addNotification('Select at least one tracker before saving', 'error')
      return
    }
    await runBusy(async () => {
      try {
        const payload: ReviewTaskWritePayload = {
          floor_ids: currentDraft.floor_ids,
          tracker_ids: currentDraft.tracker_ids,
          assignee_ids: currentDraft.assignee_ids,
          point_filter: currentDraft.point_filter,
          point_sequence: currentDraft.point_sequence,
          ai_assist: currentDraft.ai_assist,
        }
        const updated = await updateReviewTask(projectId, editingId, payload)
        replaceTask(updated)
        cancelEdit()
      } catch {
        addNotification('Unable to save review task', 'error')
      }
    })
  }

  const patchTaskBatch = (
    taskId: number,
    ai_batch: ReviewTask['ai_batch'],
  ) => {
    setTasks(prev =>
      prev.map(task => (task.id === taskId ? { ...task, ai_batch } : task)),
    )
  }

  const onStartBatch = async (task: ReviewTask) => {
    if (!task.ai_assist) {
      addNotification('Enable AI-Assist before running a batch', 'error')
      return
    }
    setBusyBatchId(task.id)
    try {
      const result = await startReviewTaskAiBatch(projectId, task.id, {
        verify_installed: task.ai_batch?.verify_installed ?? false,
        send_examples: task.ai_batch?.send_examples ?? true,
      })
      patchTaskBatch(task.id, result.batch)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to start batch'
      addNotification(message, 'error')
    } finally {
      setBusyBatchId(null)
    }
  }

  const onStopBatch = async (task: ReviewTask) => {
    setBusyBatchId(task.id)
    try {
      const result = await stopReviewTaskAiBatch(projectId, task.id)
      patchTaskBatch(task.id, result.batch)
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to stop batch'
      addNotification(message, 'error')
    } finally {
      setBusyBatchId(null)
    }
  }

  const onAddTask = async () => {
    await runBusy(async () => {
      try {
        const created = await createReviewTask(projectId, {
          status: 'not_started',
          point_filter: 'none',
          point_sequence: 'id',
          ai_assist: false,
          floor_ids: [],
          tracker_ids: [],
          assignee_ids: [],
        })
        replaceTask(created)
        setStatusFilter('all')
        adjustPendingReviewTaskCount(1)
        startEdit(created, true)
      } catch {
        addNotification('Unable to create review task', 'error')
      }
    })
  }

  const onClone = async (task: ReviewTask) => {
    await runBusy(async () => {
      try {
        const cloned = await cloneReviewTask(projectId, task.id)
        replaceTask(cloned)
        if (isPendingReviewStatus(cloned.status)) {
          adjustPendingReviewTaskCount(1)
        }
        setOpenMenuId(null)
      } catch {
        addNotification('Unable to clone review task', 'error')
      }
    })
  }

  const onSetStatus = async (task: ReviewTask, status: ReviewTaskStatus) => {
    await runBusy(async () => {
      try {
        const updated = await setReviewTaskStatus(projectId, task.id, status)
        replaceTask(updated)
        const wasPending = isPendingReviewStatus(task.status)
        const nowPending = isPendingReviewStatus(updated.status)
        if (wasPending && !nowPending) {
          adjustPendingReviewTaskCount(-1)
        } else if (!wasPending && nowPending) {
          adjustPendingReviewTaskCount(1)
        }
        setOpenMenuId(null)
      } catch {
        addNotification('Unable to update task status', 'error')
      }
    })
  }

  const onConfirmDelete = async () => {
    if (!deleteTask) {
      return
    }
    await runBusy(async () => {
      try {
        await deleteReviewTask(projectId, deleteTask.id)
        setTasks(current => current.filter(task => task.id !== deleteTask.id))
        if (isPendingReviewStatus(deleteTask.status)) {
          adjustPendingReviewTaskCount(-1)
        }
        if (editingId === deleteTask.id) {
          cancelEdit()
        }
      } catch {
        addNotification('Unable to delete review task', 'error')
      } finally {
        setDeleteTask(null)
      }
    })
  }

  const onStartContinue = async (task: ReviewTask) => {
    await runBusy(async () => {
      try {
        const updated = await setReviewTaskStatus(
          projectId,
          task.id,
          'in_progress',
        )
        replaceTask(updated)
        history.push(generateReviewTaskPath(projectId, String(task.id)))
      } catch {
        addNotification('Unable to start review task', 'error')
      }
    })
  }

  const renderMultiSelect = (
    key: string,
    selectedIds: number[],
    options: { id: number; label: string }[],
    onChange: (ids: number[]) => void,
    display: 'names' | 'count' = 'names',
    joinWithSpaces?: boolean,
    optionsConfig?: {
      allowEmptyAll?: boolean
      emptyLabel?: string
      allowSelectAll?: boolean
    },
  ) => {
    const allowEmptyAll = optionsConfig?.allowEmptyAll !== false
    const allowSelectAll = Boolean(optionsConfig?.allowSelectAll)
    const emptyLabel = optionsConfig?.emptyLabel ?? 'All'
    const allSelected =
      options.length > 0 && selectedIds.length === options.length
    const label =
      display === 'count'
        ? selectedIds.length
          ? String(selectedIds.length)
          : emptyLabel
        : idsLabel(selectedIds, options, emptyLabel, joinWithSpaces)
    const open = openDropdown === key
    return (
      <CellRelative data-review-popover>
        <DropdownToggle
          type="button"
          onClick={() => setOpenDropdown(open ? null : key)}
        >
          {label}
        </DropdownToggle>
        {open && (
          <CheckListPop>
            {allowEmptyAll && (
              <CheckLabel>
                <input
                  type="checkbox"
                  checked={selectedIds.length === 0}
                  onChange={() => onChange([])}
                />
                All
              </CheckLabel>
            )}
            {allowSelectAll && (
              <CheckLabel>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() =>
                    onChange(
                      allSelected ? [] : options.map(option => option.id),
                    )
                  }
                />
                Select all
              </CheckLabel>
            )}
            {options.map(option => (
              <CheckLabel key={option.id}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(option.id)}
                  onChange={() => onChange(toggleId(selectedIds, option.id))}
                />
                {option.label}
              </CheckLabel>
            ))}
          </CheckListPop>
        )}
      </CellRelative>
    )
  }

  const renderChoiceSelect = (
    key: string,
    value: string,
    options: { id: string; label: string }[],
    onChange: (id: string) => void,
  ) => {
    const label = options.find(option => option.id === value)?.label ?? value
    const open = openDropdown === key
    return (
      <CellRelative data-review-popover>
        <DropdownToggle
          type="button"
          onClick={() => setOpenDropdown(open ? null : key)}
        >
          {label}
        </DropdownToggle>
        {open && (
          <CheckListPop>
            {options.map(option => (
              <CheckLabel key={option.id}>
                <input
                  type="checkbox"
                  checked={value === option.id}
                  onChange={() => {
                    onChange(option.id)
                    setOpenDropdown(null)
                  }}
                />
                {option.label}
              </CheckLabel>
            ))}
          </CheckListPop>
        )}
      </CellRelative>
    )
  }

  return (
    <PageWrap>
      <HeaderRow>
        <Title>Task List</Title>
        {isAdmin && (
          <AddProjectButton
            icon={iconPlus}
            text="Add Task"
            onClick={() => {
              if (!busy) {
                void onAddTask()
              }
            }}
          />
        )}
      </HeaderRow>
      <FilterRow>
        {STATUS_FILTERS.map(filter => (
          <FilterLink
            key={filter.id}
            type="button"
            $active={listMode === 'status' && statusFilter === filter.id}
            onClick={() => {
              setListMode('status')
              setStatusFilter(filter.id)
            }}
          >
            {filter.label}
          </FilterLink>
        ))}
        {isAdmin ? (
          <FilterLink
            type="button"
            $active={listMode === 'ai_assist'}
            onClick={() => setListMode('ai_assist')}
          >
            AI Assist
          </FilterLink>
        ) : null}
      </FilterRow>
      {listMode === 'ai_assist' && isAdmin ? (
        <ReviewAiAssistTab projectId={projectId} />
      ) : loading ? (
        <LoadingIndicator />
      ) : (
        <>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Status</Th>
                  <Th>Task ID</Th>
                  {!isAdmin && <Th>Progress</Th>}
                  <Th>Dates</Th>
                  <Th>Levels</Th>
                  <Th>Trackers</Th>
                  <Th>Point Filters</Th>
                  <Th>Point Sequence</Th>
                  <Th>Assignee</Th>
                  <Th>AI-Assist</Th>
                  {isAdmin ? (
                    <>
                      <Th>Batch Run</Th>
                      <Th>Batch Status</Th>
                      <Th>Batch Edit</Th>
                      <Th>Stats</Th>
                      <Th>Edit</Th>
                      <Th>Log</Th>
                      {!editingId && <Th>Other</Th>}
                    </>
                  ) : (
                    <Th>Act</Th>
                  )}
                </tr>
              </thead>
              <tbody>
                {tasks.length === 0 && (
                  <tr>
                    <Td colSpan={isAdmin ? (editingId ? 15 : 16) : 11}>
                      <EmptyState>No review tasks</EmptyState>
                    </Td>
                  </tr>
                )}
                {tasks.map(task => {
                  const editing = isAdmin && editingId === task.id && !!draft
                  const rowDraft = editing ? draft : null
                  return (
                    <tr key={task.id}>
                      <Td>
                        <StatusDot $status={task.status} />
                      </Td>
                      <Td>{task.id}</Td>
                      {!isAdmin && (
                        <Td>{formatReviewTaskProgress(task)}</Td>
                      )}
                      <Td>
                        <DateStack>
                          <span>
                            <em>Created</em>
                            {formatCompactTaskDate(task.registered_on)}
                          </span>
                          <span>
                            <em>Started</em>
                            {formatCompactTaskDate(task.started_at)}
                          </span>
                          <span>
                            <em>Done</em>
                            {formatCompactTaskDate(task.completed_at)}
                          </span>
                        </DateStack>
                      </Td>
                      <Td>
                        {rowDraft
                          ? renderMultiSelect(
                              `${task.id}-floors`,
                              rowDraft.floor_ids,
                              floors,
                              floorIds =>
                                setDraft(current =>
                                  current
                                    ? { ...current, floor_ids: floorIds }
                                    : current,
                                ),
                              'names',
                              true,
                            )
                          : task.floors_label}
                      </Td>
                      <Td>
                        {rowDraft
                          ? renderMultiSelect(
                              `${task.id}-trackers`,
                              rowDraft.tracker_ids,
                              trackers,
                              trackerIds =>
                                setDraft(current =>
                                  current
                                    ? { ...current, tracker_ids: trackerIds }
                                    : current,
                                ),
                              'count',
                              false,
                              {
                                allowEmptyAll: false,
                                allowSelectAll: true,
                                emptyLabel: trackerEditLabel([]),
                              },
                            )
                          : trackerCountLabel(task.tracker_ids)}
                      </Td>
                      <Td>
                        {rowDraft
                          ? renderChoiceSelect(
                              `${task.id}-point-filter`,
                              rowDraft.point_filter,
                              POINT_FILTER_OPTIONS,
                              id =>
                                setDraft(current =>
                                  current
                                    ? {
                                        ...current,
                                        point_filter:
                                          id as TaskDraft['point_filter'],
                                      }
                                    : current,
                                ),
                            )
                          : pointFilterLabel(task.point_filter)}
                      </Td>
                      <Td>
                        {rowDraft
                          ? renderChoiceSelect(
                              `${task.id}-point-sequence`,
                              rowDraft.point_sequence,
                              POINT_SEQUENCE_OPTIONS,
                              id =>
                                setDraft(current =>
                                  current
                                    ? {
                                        ...current,
                                        point_sequence:
                                          id as TaskDraft['point_sequence'],
                                      }
                                    : current,
                                ),
                            )
                          : pointSequenceLabel(task.point_sequence)}
                      </Td>
                      <Td>
                        {rowDraft
                          ? renderMultiSelect(
                              `${task.id}-assignees`,
                              rowDraft.assignee_ids,
                              assignees.map(assignee => ({
                                id: assignee.id,
                                label: assignee.name,
                              })),
                              assigneeIds =>
                                setDraft(current =>
                                  current
                                    ? { ...current, assignee_ids: assigneeIds }
                                    : current,
                                ),
                            )
                          : task.assignees_label}
                      </Td>
                      <Td>
                        {rowDraft ? (
                          <input
                            type="checkbox"
                            checked={rowDraft.ai_assist}
                            onChange={event =>
                              setDraft(current =>
                                current
                                  ? {
                                      ...current,
                                      ai_assist: event.target.checked,
                                    }
                                  : current,
                              )
                            }
                          />
                        ) : (
                          <DisabledCheck
                            type="checkbox"
                            checked={task.ai_assist}
                            disabled
                            readOnly
                          />
                        )}
                      </Td>
                      {isAdmin ? (
                        <>
                          <Td>
                            {isAiBatchRunning(task.ai_batch) ? (
                              <BatchStopBtn
                                type="button"
                                title="Stop batch"
                                disabled={busyBatchId === task.id}
                                onClick={() => void onStopBatch(task)}
                              >
                                ■
                              </BatchStopBtn>
                            ) : (
                              <BatchRunBtn
                                type="button"
                                title={
                                  task.ai_assist
                                    ? 'Run batch'
                                    : 'Enable AI-Assist to run a batch'
                                }
                                disabled={
                                  !task.ai_assist || busyBatchId === task.id
                                }
                                onClick={() => void onStartBatch(task)}
                              >
                                ▶
                              </BatchRunBtn>
                            )}
                          </Td>
                          <Td title={formatAiBatchStatus(task.ai_batch)}>
                            {formatAiBatchStatus(task.ai_batch)}
                          </Td>
                          <Td>
                            <ActionButton
                              type="button"
                              disabled={!task.ai_assist}
                              title={
                                task.ai_assist
                                  ? 'Open AI batch task'
                                  : 'Enable AI-Assist first'
                              }
                              onClick={() =>
                                history.push(
                                  generateAdminReviewAiPath(
                                    projectId,
                                    String(task.id),
                                  ),
                                )
                              }
                            >
                              Edit
                            </ActionButton>
                          </Td>
                          <Td>
                            <CellRelative
                              onMouseEnter={() => setOpenStatsId(task.id)}
                              onMouseLeave={() => setOpenStatsId(null)}
                            >
                              <StatsInline>
                                <span>
                                  <em>Viewed</em>
                                  {formatReviewTaskProgress(task)}
                                </span>
                                <span>
                                  <em>Changes</em>
                                  {task.changes_made}
                                </span>
                              </StatsInline>
                              {openStatsId === task.id && (
                                <StatsPopover>
                                  <div>
                                    Last worker:{' '}
                                    {task.last_worked_by?.name || '—'}
                                  </div>
                                  <div>
                                    Time:{' '}
                                    {formatDurationHoursMinutes(
                                      task.active_seconds || 0,
                                    )}
                                  </div>
                                  <div>
                                    Total review time:{' '}
                                    {formatDurationHoursMinutes(
                                      task.review_active_seconds || 0,
                                    )}
                                  </div>
                                  <div>
                                    {formatMinutesPerPoint(
                                      task.active_seconds || 0,
                                      task.points_viewed,
                                    )}
                                  </div>
                                </StatsPopover>
                              )}
                            </CellRelative>
                          </Td>
                          <Td data-review-save>
                            {editing ? (
                              <>
                                <SaveButton
                                  type="button"
                                  onClick={saveEdit}
                                  disabled={busy}
                                >
                                  Save
                                </SaveButton>
                                <CancelButton
                                  type="button"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </CancelButton>
                              </>
                            ) : (
                              <IconButton
                                type="button"
                                onClick={() => startEdit(task)}
                              >
                                <img src={iconPencil} alt="Edit" />
                              </IconButton>
                            )}
                          </Td>
                          <Td>
                            <IconButton
                              type="button"
                              $disabled={task.activity_log_count === 0}
                              disabled={task.activity_log_count === 0}
                              title={
                                task.activity_log_count
                                  ? `View activity log (${task.activity_log_count})`
                                  : task.activity_log_count === 0
                                    ? 'No activity logged yet'
                                    : 'View activity log'
                              }
                              onClick={() => {
                                if (task.activity_log_count !== 0) {
                                  setLogTaskId(task.id)
                                }
                              }}
                            >
                              <img src={iconFile} alt="Log" />
                            </IconButton>
                          </Td>
                          {!editingId && (
                            <Td>
                                <CellRelative data-review-popover>
                                  <IconButton
                                    type="button"
                                    onClick={() =>
                                      setOpenMenuId(
                                        openMenuId === task.id ? null : task.id,
                                      )
                                    }
                                  >
                                    <img src={iconMenu} alt="More" />
                                  </IconButton>
                                  {openMenuId === task.id && (
                                    <Menu>
                                      <MenuItem
                                        type="button"
                                        onClick={() => onClone(task)}
                                      >
                                        Clone
                                      </MenuItem>
                                      <MenuItem
                                        type="button"
                                        onClick={() =>
                                          onSetStatus(task, 'completed')
                                        }
                                      >
                                        Mark Done
                                      </MenuItem>
                                      <MenuItem
                                        type="button"
                                        onClick={() =>
                                          onSetStatus(task, 'in_progress')
                                        }
                                      >
                                        In Progress
                                      </MenuItem>
                                      <MenuItem
                                        type="button"
                                        onClick={() =>
                                          onSetStatus(task, 'not_started')
                                        }
                                      >
                                        Not Started
                                      </MenuItem>
                                      <MenuItem
                                        type="button"
                                        onClick={() => {
                                          setOpenMenuId(null)
                                          setDeleteTask(task)
                                        }}
                                      >
                                        Delete
                                      </MenuItem>
                                    </Menu>
                                  )}
                                </CellRelative>
                              </Td>
                          )}
                        </>
                      ) : (
                        <Td>
                          {task.status !== 'completed' && (
                            <ActionButton
                              type="button"
                              disabled={busy}
                              onClick={() => onStartContinue(task)}
                            >
                              {task.status === 'in_progress'
                                ? 'Continue'
                                : 'Start'}
                            </ActionButton>
                          )}
                        </Td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </TableScroll>
          {hasMore && (
            <LoadMoreWrap>
              {loadingMore ? (
                <LoadingIndicator size={40} />
              ) : (
                <AddProjectButton text="Load More" onClick={loadMoreTasks} />
              )}
            </LoadMoreWrap>
          )}
        </>
      )}
      <ConfirmationModal
        isOpen={!!deleteTask}
        setIsOpen={open => {
          if (!open && !busy) {
            setDeleteTask(null)
          }
        }}
        message="Delete this review task?"
        onConfirm={onConfirmDelete}
        confirmButtonAltText="Delete"
        closeOnConfirm={false}
        confirmDisabled={busy}
        cancelDisabled={busy}
      />
      {logTaskId != null && projectId ? (
        <ReviewActivityLogModal
          projectId={projectId}
          taskId={logTaskId}
          onClose={() => setLogTaskId(null)}
        />
      ) : null}
      {busy && (
        <ListBusyOverlay>
          <LoadingIndicator />
        </ListBusyOverlay>
      )}
    </PageWrap>
  )
}
